import _ from 'lodash';
import moment from 'moment';
import MoviesService from './movies.service';
import ImdbMovie from './imdb-movie.schema';
import ImdbLegacyHtmlParser from './imdb-legacy-html-parser';
import ImdbHtmlLinkedDataParser from './imdb-html-linkeddata-parser';


// inspired by imdb scraper found here:
// https://github.com/mhsjlw/imdb


export default class ImdbHtmlParser {

  static get ValidContentRatings() {
    return MoviesService.MPAARatingOptions;
  }

  imdbMovieId = null;
  doc = null;
  verbose = true;

  selectors = {
    linkedDataScript:   'script[type="application/ld+json"]',
    title:              '[data-testid="hero__pageTitle"], [data-testid="hero-title-block__title"]',
    year:               '[data-testid="hero__pageTitle"] ~ div ul li:nth-child(1) a, [class*="TitleBlock__TitleMetaDataContainer-"] > ul > li:nth-child(1) > span[class*="TitleBlockMetaData__ListItemText-"]',
    contentRating:      '[data-testid="hero__pageTitle"] ~ div ul li:nth-child(2) a, [class*="TitleBlock__TitleMetaDataContainer-"] > ul > li:nth-child(2) > span[class*="TitleBlockMetaData__ListItemText-"]',
    runtime:            '[data-testid="hero__pageTitle"] ~ div ul li:nth-child(3), [class*="TitleBlock__TitleMetaDataContainer-"] > ul > li:nth-child(3)',
    synopsis:           '[data-testid="plot"] [data-testid="plot-xl"], [data-testid="plot"] [data-testid="plot-l"], [data-testid="plot"] span[data-testid="plot-xs_to_m"]',
    starRating:         '[data-testid="hero-rating-bar__aggregate-rating__score"] span:first-child, [data-testid="hero-title-block__aggregate-rating__score"] [class*="AggregateRatingButton__RatingScore-"]',
    poster:             '[data-testid="hero-media__poster"] img, div[class*="Media__PosterContainer-"] img.ipc-image',
    metascore:          null,
    creditLabel:        '[data-testid="title-pc-principal-credit"] .ipc-metadata-list-item__label',
    creditLabel2:       '[data-testid="title-pc-principal-credit"] .ipc-metadata-list-item__label',
    creditDetails:      '[data-testid="title-pc-principal-credit"]',
    creditDetails2:     '[data-testid="title-pc-principal-credit"]',
    creditDetailItem:   '.ipc-metadata-list-item__list-content-item',
    genre:              '[data-testid="genres"] a span, [data-testid="genres"] .ipc-chip__text, [class*="GenresAndPlot__ContentParent-"] .ipc-chip__text',
    releaseDate:        '[data-testid="title-details-section"] [data-testid="title-details-releasedate"] .ipc-metadata-list-item__list-content-item',
};

  constructor(rawHtml, imdbMovieId = null) {
    this.imdbMovieId = imdbMovieId;
    this.doc = this.createHtmlDocumentDom(rawHtml);
  }

  createHtmlDocumentDom(rawHtml) {
    let doc = null;
    try {
      if (_.isString(rawHtml) && !_.isEmpty(rawHtml)) {
        let html = _.trim(rawHtml.replace(/(\r\n|\n|\r)/gm, '').replace(/ +(?= )/g, ''));
        if (!_.isEmpty(html)) {
          let parser = new DOMParser();
          doc = parser.parseFromString(html, 'text/html');
        }
      }
    } catch (ex) {
      console.error('ImdbHtmlParser.createHtmlDocumentDom error: ', ex);
    }
    return doc;
  }

  findAndParseLinkedJson() {
    let data = null;
    try {
      let scriptEl = this.findElement(this.selectors.linkedDataScript);
      let jsonStr = scriptEl? scriptEl.innerHTML: null;
      if (jsonStr) {
        data = JSON.parse(jsonStr);
      }
    } catch (ex) {}
    return data;
  }

  parseAndMergeAll() {
    let mergedMovie = null;
    if (this.doc) {
      let linkedMovie = null;
      let linkedData = this.findAndParseLinkedJson();
      if (!_.isEmpty(linkedData)) {
        let linkedDataParser = new ImdbHtmlLinkedDataParser(linkedData, this.imdbMovieId);
        linkedMovie = linkedDataParser.parseAll();
      }

      let legacyParser = new ImdbLegacyHtmlParser(this.doc, this.imdbMovieId);
      let legacyMovie = legacyParser.parseAll();

      let htmlMovie = this.parseAllHtmlSources();

      if (this.verbose) {
        console.log('IMDB html movie data: ');
        console.log(JSON.stringify(htmlMovie, null, 2));
        console.log();

        console.log('IMDB legacy movie data: ');
        console.log(JSON.stringify(legacyMovie, null, 2));
        console.log();

        console.log('IMDB linked-data movie data: ');
        console.log(JSON.stringify(linkedMovie, null, 2));
        console.log();
      }

      if (htmlMovie || legacyMovie || linkedMovie) {

        let _hasValue = (v) => {
          return (v !== null && v !== undefined && v !== "");
        };

        linkedMovie = _.pickBy(linkedMovie, _hasValue);
        legacyMovie = _.pickBy(legacyMovie, _hasValue);
        htmlMovie = _.pickBy(htmlMovie, _hasValue);

        mergedMovie = new ImdbMovie();
        mergedMovie = _.merge(mergedMovie, linkedMovie, legacyMovie, htmlMovie);
      }
    }
    return mergedMovie;
  }

  parseAllHtmlSources() {
    let movie = null;
    if (this.doc) {
      movie = new ImdbMovie();
      movie.imdb_id = this.imdbMovieId;
      movie.title = this.getTitle();
      movie.release_date = this.getReleaseDate();
      movie.release_year = this.getYear();

      let posterUrls = this.getPosterUrls();
      movie.imdb_img = _.first(posterUrls) || null;
      movie.imdb_imgs = posterUrls;

      movie.rating = this.getContentRating();
      movie.duration_mins = this.getRuntimeMinutes();
      movie.synopsis = this.getSynopsis();
      movie.star_rating = this.getStarRating();
      movie.metascore = this.getMetaScore();

      movie.all_genres = this.getGenres();
      movie.all_writers = this.getWriters();
      movie.all_directors = this.getDirectors();
      movie.all_cast = this.getCast();

      movie.genre = _.join(movie.all_genres, ', ');
      movie.cast = _.join(movie.all_cast, ', ');
    }
    return movie;
  }

  getTitle() {
    let str = this.findInnerText(this.selectors.title);
    if (str) {
      str = _.trim(_.replace(str, /\(\d+\)/g, ''));
    }
    return str;
  }

  getSynopsis() {
    let str = this.findInnerText(this.selectors.synopsis);
    if (str) {
      str = _.trim(str);
    }
    return !_.isEmpty(str)? str: null;
  }

  getYear() {
    let str = this.findInnerText(this.selectors.year);
    return this._parseInt(str);
  }

  getReleaseDate() {
    let str = this.findInnerText(this.selectors.releaseDate);
    if (str && _.isString(str)) {
      str = _.trim(_.replace(str, /\(.*/, ''));
    }

    let datestr = null;
    if (!_.isEmpty(str)) {
      let tmpDate = null;
      try {
        tmpDate = moment(new Date(str));
      } catch (ex) {}
      if (tmpDate && tmpDate.isValid()) {
        datestr = tmpDate.format('YYYY-MM-DD');   // iso date only (no time)
      }
    }
    return datestr;
  }

  getPosterUrls() {
    let poster1 = this.findAttr(this.selectors.poster, 'src');
    let poster1ResizedA = _.replace(poster1, /\._[^.]+_(\.\w+)$/, '$1');    // hacked to grab resized media at 1000px tall.  Updated 12/14/2020 to account for new URL syntax
    // let poster1ResizedB = _.replace(poster1, /@\._V1.*\.(\w+)$/, '@._V1_SY1000_AL_.$1');    // hacked to grab resized media at 1000px tall

    return _.chain([ poster1ResizedA, poster1 ])
      .map(_.trim)
      .uniq()
      .compact()
      .value();
  }

  getRuntimeMinutes() {
    let mins = null;
    let str = this.findInnerText(this.selectors.runtime);
    if (str) {
      let m = str.match(/(?:(\d+)h(?:rs)?)?\s+(?:(\d+)m(?:ins)?)?/i);
      if (m) {
        let tmpHours = m[1];
        let tmpMins = m[2];

        tmpHours = this._parseInt(tmpHours, 0);
        tmpMins = this._parseInt(tmpMins, 0);
        if (!_.isFinite(tmpHours)) { tmpHours = 0; }
        if (!_.isFinite(tmpMins)) { tmpMins = 0; }
        if (tmpHours > 0 || tmpMins > 0) {
          mins = tmpHours * 60 + tmpMins;
        }
      }
    }

    return mins;
  }

  getCast() {
    return this._getCreditType(/Stars?/i);
  }

  getWriters() {
    return this._getCreditType(/Writers?/i);
  }

  getDirectors() {
    return this._getCreditType(/Directors?/i);
  }

  _getCreditType(matchRegex) {
    // NOTE: there are multiple sections which match the same selectors.
    // find the one that has the correct label

    let creditLabels = this.findInnerTexts(this.selectors.creditLabel);
    if (_.isEmpty(creditLabels)) {
      creditLabels = this.findInnerTexts(this.selectors.creditLabel2);
    }
    let targetIdx = _.findIndex(creditLabels, v => _.toString(v).match(matchRegex));

    let creditDetails = this.findElements(this.selectors.creditDetails);
    if (_.isEmpty(creditDetails)) {
      creditDetails = this.findElements(this.selectors.creditDetails2)
    }
    creditDetails = creditDetails = _.get(creditDetails, targetIdx);
    if (creditDetails) {
      creditDetails = creditDetails.querySelectorAll(this.selectors.creditDetailItem);
    } else {
      creditDetails = null;
    }

    let items = _.chain(creditDetails)
      .map(el => _.trim(el && el.textContent))
      .uniq()
      .compact()
      .slice(0, 3)    // only grab the first 3 items
      .value();

    return !_.isEmpty(items)? items: null;
  }

  getContentRating() {
    let str = this.findInnerText(this.selectors.contentRating);
    let parts = _.chain(str)
      .trim()
      .split('|')
      .map(_.trim)
      .compact()
      .value();

    let retval = _.nth(parts, 0);
    let rating = !_.isEmpty(retval)? retval: null;

    let validRatings = this.constructor.ValidContentRatings;
    if (!_.isEmpty(validRatings)) {
      let validRating = _.find(validRatings, v => {
        return (_.toLower(v.label) === _.toLower(rating));
      });
      rating = _.get(validRating, 'value') || null;
    }

    return rating;
  }

  getStarRating() {
    let str = this.findInnerText(this.selectors.starRating);
    return this._parseFloat(str);
  }

  getMetaScore() {
    // let str = this.findInnerText(this.selectors.metascore);
    // return this._parseFloat(str);
    return null;
  }

  getGenres() {
    let strs = this.findInnerTexts(this.selectors.genre);
    let genreItems = _.chain(strs)
      .map(_.trim)
      .uniq()
      .compact()
      .slice(0, 3)    // only grab the first 3 items
      .value();
    return !_.isEmpty(genreItems)? genreItems: null;
  }


  // DOM helpers

  findElement(selectors) {
    return this.doc.querySelector(selectors);
  }

  findElements(selectors) {
    let elements = [];
    let elNodeList = this.doc.querySelectorAll(selectors);
    if (elNodeList && elNodeList.length > 0) {
      _.each(elNodeList, v => {
        elements.push(v);
      });
    }
    return elements;
  }

  findInnerText(selectors) {
    let str = null;
    let el = this.findElement(selectors);
    try {
      if (el) {
        str = _.trim(el.textContent);
      }
    } catch (ex) {}
    return str;
  }

  findInnerTexts(selectors) {
    let elements = this.findElements(selectors);
    return _.map(elements, el => {
      return _.trim(el.textContent);
    });
  }

  findAttr(selectors, attrName) {
    let str = null;
    let el = this.findElement(selectors);
    try {
      if (el) {
        str = _.trim(el.getAttribute(attrName));
      }
    } catch (ex) {}
    return str;
  }

  findAttrs(selectors, attrName) {
    let elements = this.findElements(selectors);
    return _.map(elements, el => {
      return _.trim(el.getAttribute(attrName));
    });
  }

  _parseInt(str, defaultValue = null) {
    let retval = defaultValue;
    if (str) {
      let tmp = parseInt(str);
      if (_.isFinite(tmp)) {
        retval = tmp;
      }
    }
    return retval;
  }

  _parseFloat(str, defaultValue = null) {
    let retval = defaultValue;
    if (str) {
      let tmp = parseFloat(str);
      if (_.isFinite(tmp)) {
        retval = tmp;
      }
    }
    return retval;
  }

}

